import React from 'react';

import { useMe } from '$/hooks/useMe';

import type { AddOn } from '../AddOns';
import { Per, SubscriptionNote } from './Components';
import { UpdateAddOnType, useAddOnsContext } from './AddOnsContext';

import { logger } from '$/utils/logger';
import { BillingCycle, SubscriptionSubstatus } from '$/GraphQL/schema/types';
import { formatCurrency } from '$/utils/formatCurrency';
import { usePlanPageContext } from '../PlanPage';
import Button from '$/components/ui/Button';

export const getListComponent = (addOn: AddOn): React.FC => {
  const ListComponent: React.FC = () => {
    const me = useMe();
    const { openModal } = useAddOnsContext();
    const { subscriptionInfo } = usePlanPageContext();

    const onClick = React.useCallback(() => {
      if (!addOn.priceTier) {
        logger.error('Missing priceTier in add-on ', addOn.name);
        return;
      }
      openModal({
        addOn,
        tierId: addOn.priceTier.tierId,
        type: addOn.activeUntil ? UpdateAddOnType.Resubscribe : UpdateAddOnType.Unsubscribe,
      });
    }, [openModal]);

    const button = React.useMemo(() => {
      if (subscriptionInfo.subStatus === SubscriptionSubstatus.Pending) {
        return null;
      }
      if (addOn.activeUntil) {
        return (
          <Button.blueText className='text-sm' onClick={onClick}>
            Resubscribe
          </Button.blueText>
        );
      }

      if (subscriptionInfo.isCancelled) return null;
      return (
        <Button.redText className='text-sm' onClick={onClick}>
          Unsubscribe
        </Button.redText>
      );
    }, [onClick, subscriptionInfo.isCancelled, subscriptionInfo.subStatus]);

    const note = React.useMemo(() => {
      if (addOn.activeUntil) return `Will remain active until ${addOn.activeUntil.format('P')}`;
      return null;
    }, []);

    const freeUntil = addOn.getFreeUntil(me);

    return (
      <tr>
        <td>
          {addOn.name}
          {note && (
            <>
              <br />
              <SubscriptionNote>{note}</SubscriptionNote>
            </>
          )}
          {freeUntil && !note && addOn.price === 0 && (
            <>
              <br />
              <SubscriptionNote>
                Free until {freeUntil.format('P')}, then{' '}
                {addOn.getFormattedPrice(me.team.billingCycle)}/$
                {me.team.billingCycle === BillingCycle.Month ? 'month' : 'year'}
              </SubscriptionNote>
            </>
          )}
        </td>
        {[addOn.priceTier?.monthlyPrice, addOn.priceTier?.annualPricePerMonth].map((price) => {
          const content = addOn.activeUntil ? (
            '—'
          ) : (
            <>
              {formatCurrency(price ?? 0)}
              <Per />
            </>
          );
          return (
            <td className='price' key={price}>
              {content}
              <div className='leading-1e add-on-button'>{button}</div>
            </td>
          );
        })}
      </tr>
    );
  };
  return ListComponent;
};
